import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { adKeys, useBillboardsQuery } from "../api/advertisingQueries";
import { msUntilNextUtcMidnight } from "../advertise/utcDays";
import { serverNow } from "../lib/serverClock";
import type { TownSession } from "./townSession";

/** Wait this long after 00:00 UTC before asking (the server's read path switches exactly at midnight). */
export const MIDNIGHT_GRACE_MS = 3_000;
/** Spread the post-midnight refresh of many open towns over this window. */
export const MIDNIGHT_JITTER_MS = 20_000;

/** Delay until the next post-midnight refresh, by the server-adjusted clock. */
export function nextBillboardRefreshDelay(nowMs: number, jitter01: number): number {
  const jitter = Math.max(0, Math.min(1, jitter01)) * MIDNIGHT_JITTER_MS;
  return msUntilNextUtcMidnight(nowMs) + MIDNIGHT_GRACE_MS + Math.round(jitter);
}

/**
 * Mirrors `GET /billboards` into the renderer (`scene.setBillboards`): on load, just after every
 * UTC midnight (when rentals start and end), when a billboard / creative notification arrives (the
 * notifier invalidates the query), and every few minutes as a safety net for staff revocations.
 */
export function useBillboardMirror(session: TownSession | null, enabled: boolean): void {
  const qc = useQueryClient();
  const q = useBillboardsQuery(enabled);
  const data = q.data;

  useEffect(() => {
    if (session && data) session.setBillboards(data.items);
  }, [session, data]);

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      timer = setTimeout(() => {
        void qc.invalidateQueries({ queryKey: adKeys.billboards });
        void qc.invalidateQueries({ queryKey: adKeys.availabilityAll });
        schedule();
      }, nextBillboardRefreshDelay(serverNow(), Math.random()));
    };
    schedule();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [enabled, qc]);
}
